/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useRef } from 'react';

import { cssMap, jsx } from '@compiled/react';
import { useIntl } from 'react-intl-next';

import type { JSONDocNode } from '@atlaskit/editor-json-transformer';
import { Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { FloatingContainer } from '../../components/FloatingContainer/FloatingContainer';
import { PromptEditorWrapper } from '../../components/PromptEditorWrapper/PromptEditorWrapper';
import type { PromptEditor } from '../../components/PromptEditorWrapper/PromptEditorWrapper';

import { useEscapeKey, useSubmitControls } from './hooks';
import messages from './messages';

type Props = {
	textInput?: string;
	adfInput?: JSONDocNode;
	onTextChange: (value: string) => void;
	onADFChange?: (value: JSONDocNode) => void;
	submit: () => void;
	cancel: () => void;
	placeholder?: string;
	PromptEditor: PromptEditor;
};

const container = cssMap({
	padding: {
		paddingInline: token('space.200'),
		paddingBlock: token('space.150'),
	},
});

export const PromptScreen = ({
	textInput,
	adfInput,
	onTextChange,
	onADFChange,
	submit,
	cancel,
	PromptEditor,
}: Props) => {
	const intl = useIntl();
	const screenRef = useRef<HTMLDivElement | null>(null);
	useEscapeKey(screenRef, cancel);
	const { onKeyDown } = useSubmitControls(submit, textInput);
	const placeholder = `${intl.formatMessage(messages.promptPlaceholder)}...`;

	return (
		<FloatingContainer
			ref={screenRef}
			data-testid="prompt-form-screen"
			disablePadding
			rainbowBorder={false}
			enableMaxHeight
		>
			<Inline xcss={container.padding}>
				<PromptEditorWrapper
					autoFocus
					PromptEditor={PromptEditor}
					type="user-input"
					defaultValue={adfInput}
					onInputChange={onTextChange}
					onADFChange={onADFChange}
					placeholder={placeholder}
					enableLinks
					onKeyDown={onKeyDown}
					submit={submit}
				/>
			</Inline>
		</FloatingContainer>
	);
};
